import { Injectable } from '@angular/core';
import { BaseProvider } from '../base';
import { API } from '../api';
import { DatasetProvider } from '../dataset';
import { ModalProvider } from '../popup/modal';
import { CanvasProvider } from './canvas';
import { BoostProvider } from '../boost';
import { BrainBankErrorCode } from '../error-code';
import { TranslateService } from '@ngx-translate/core';

export interface Rectangle {
    width: number;
    height: number;
    x: number;
    y: number;
}

@Injectable()
export class RegionOfInterestProvider extends BaseProvider {
    constructor(
        private api: API,
        private datasetProvider: DatasetProvider,
        private modalProvider: ModalProvider,
        private canvasProvider: CanvasProvider,
        private boostProvider: BoostProvider,
        private translate: TranslateService
    ) {
        super();
    }

    public async saveAndConfirmRetrain(datasetIdentifier: string): Promise<boolean> {
        try {
            const roi = this.canvasProvider.exportROI();
            if (!roi) {
                return false;
            }

            await this.updateROI(datasetIdentifier, roi);
            await this.datasetProvider.reloadDataset(datasetIdentifier, true);

            const dataset = this.datasetProvider.getDatasetById(datasetIdentifier);

            if (dataset && dataset.hasPolygons()) {
                const confirmationResult = await this.presentConfirmationModal(
                    {
                        title: this.translate.instant('Retrain and Redeploy'),
                        message: this.translate.instant(
                            'You have defined and saved a Region of Interest for this Brain. In order for the Region of Interest to take effect you must Retrain the Brain. <strong>Before retraining, page through the images and make sure the ROI is big enough to encapsulate the subject in all images.</strong>'
                        ),
                        discardBtnText: this.translate.instant('Retrain Later'),
                        confirmBtnText: this.translate.instant('Retrain')
                    },
                    'roi-save-and-retrain-modal'
                );

                if (confirmationResult && confirmationResult.shouldRetrain) {
                    await this.retrain(datasetIdentifier, confirmationResult.sendEmail);
                }
            }

            return true;
        } catch (e) {
            throw e;
        }
    }

    public async confirmUnsavedChangesAndRetrain(datasetIdentifier: string): Promise<boolean> {
        const dataset = this.datasetProvider.getDatasetById(datasetIdentifier);
        if (!dataset) {
            return false;
        }

        if (!dataset.hasPolygons()) {
            const roi = this.canvasProvider.exportROI();
            if (!roi) {
                return false;
            }

            await this.updateROI(datasetIdentifier, roi);
            return true;
        }

        const confirmationResult = await this.presentConfirmationModal(
            {
                title: this.translate.instant('Unsaved Changes'),
                message: this.translate.instant(
                    'There are unsaved changes to your Region of Interest. Would you like to save those changes? If so you will need to retrain the Brain. This may effect accuracy results.'
                ),
                discardBtnText: this.translate.instant('Discard Changes'),
                confirmBtnText: this.translate.instant('Save & Retrain')
            },
            'roi-confirm-changes'
        );

        if (confirmationResult && confirmationResult.shouldRetrain) {
            await this.saveAndRetrain(datasetIdentifier, confirmationResult);
            return false;
        } else if (confirmationResult && !confirmationResult.shouldRetrain) {
            // Explicitly revert the ROI to its previous state.
            // This is in case we have both unsaved ROI and other unsaved changes.
            const roiData = await this.load(datasetIdentifier);

            if (roiData) {
                await this.canvasProvider.drawROI(roiData);
            } else {
                await this.canvasProvider.deleteROI();
            }

            await this.canvasProvider.markROIAsSaved();
        }

        return true;
    }

    public async saveAndRetrain(datasetIdentifier: string, confirmationResult?: any) {
        const roi = this.canvasProvider.exportROI();
        if (!roi) {
            return;
        }

        await this.updateROI(datasetIdentifier, roi);
        await this.datasetProvider.reloadDataset(datasetIdentifier, true);
        await this.retrain(datasetIdentifier, confirmationResult && confirmationResult.sendEmail);
    }

    public async confirmDeleteAndRetrain(datasetIdentifier: string): Promise<void> {
        const dataset = this.datasetProvider.getDatasetById(datasetIdentifier);
        if (!dataset) {
            return;
        }

        if (dataset.hasPolygons()) {
            const confirmationResult = await this.presentConfirmationModal(
                {
                    title: this.translate.instant('Delete and Retrain'),
                    message: this.translate.instant(
                        'Are you sure you want to delete the region of interest from this Brain? If so you will need to retrain the Brain. This may effect accuracy results.'
                    ),
                    discardBtnText: this.translate.instant('Cancel'),
                    confirmBtnText: this.translate.instant('Delete & Retrain')
                },
                'roi-delete-and-retrain-modal'
            );

            if (confirmationResult && confirmationResult.shouldRetrain) {
                await this.deleteROI(datasetIdentifier);
                await this.datasetProvider.reloadDataset(datasetIdentifier, true);
                await this.retrain(datasetIdentifier, confirmationResult.sendEmail);
            }
        } else {
            await this.deleteROI(datasetIdentifier);
        }
    }

    public async load(datasetIdentifier: string, denormalize: boolean = false): Promise<number[][] | null> {
        try {
            const result = await this.api.get(`datasets/${datasetIdentifier}/rois?denormalize=${denormalize}`);
            if (result && result.message && result.message.roi && result.message.roi.data) {
                return result.message.roi.data;
            }
        } catch (err) {
            if (err.error && err.error.code == BrainBankErrorCode.RoiNotFound) {
                return null;
            } else {
                throw err;
            }
        }

        return null;
    }

    private async deleteROI(datasetIdentifier: string): Promise<void> {
        try {
            await this.api.delete(`datasets/${datasetIdentifier}/rois`);

            this.canvasProvider.deleteROI();
            this.canvasProvider.markROIAsSaved();
        } catch (err) {
            throw err;
        }
    }

    private async updateROI(datasetIdentifier: string, points: number[][]): Promise<void> {
        try {
            const result = await this.api.post(`datasets/${datasetIdentifier}/rois`, {
                data: points
            });

            if (result && result.success && result.message && result.message.roi && result.message.roi.data) {
                this.canvasProvider.markROIAsSaved();
                return result.message.roi.data;
            }
        } catch (err) {
            throw err;
        }
    }

    public toRectangle(data: number[][]): Rectangle {
        let minX: number | null = null;
        let minY: number | null = null;
        let maxX: number | null = null;
        let maxY: number | null = null;

        for (const point of data) {
            if (minX == null || point[0] < minX) {
                minX = point[0];
            }

            if (maxX == null || point[0] > minX) {
                maxX = point[0];
            }

            if (minY == null || point[1] < minY) {
                minY = point[1];
            }

            if (maxY == null || point[1] > minY) {
                maxY = point[1];
            }
        }

        if (minX == null || minY == null || maxX == null || maxY == null) {
            throw new Error(`Unable to create a rectangle from the given points: (${minX},${minY}),(${maxX},${maxY})`);
        }

        return {
            width: maxX - minX,
            height: maxY - minY,
            x: minX,
            y: minY
        };
    }

    private async retrain(datasetIdentifier: string, sendEmail: boolean): Promise<void> {
        await this.boostProvider.retrain(datasetIdentifier, sendEmail);
        this.boostProvider.showRetrainProgress(datasetIdentifier);
    }

    private presentConfirmationModal(
        data: {
            title: string;
            message: string;
            discardBtnText: string;
            confirmBtnText: string;
        },
        cssClass: string
    ): Promise<any> {
        return new Promise(resolve => {
            const modal = this.modalProvider.createModal('RoiConfirmChangesModal', data, {
                cssClass
            });

            modal.onDidDismiss(result => {
                resolve(result);
            });
            modal.present();
        });
    }

    /**
     * Create a string representing SVG commands to create the region of interest's overlay (minus the cutout in the center).
     * @param roiData The normalized coordinates (in percentiles) of the ROI
     * @param width The width of the current image asset
     * @param height The height of the current image asset
     */
    public buildRoiShape(roiData: number[][], width: number, height: number): string {
        if (!roiData) {
            return '';
        }

        const [topLeft, topRight, bottomLeft, bottomRight] = roiData.sort((pointL: number[], pointR: number[]) => {
            const [xL, yL] = pointL;
            const [xR, yR] = pointR;

            return xL - xR || yL - yR;
        });

        return `M 0 0 L ${width} 0 ${width} ${height} 0 ${height}
                        M ${width * topLeft[0]} ${height * topLeft[1]}
                        L ${width * topRight[0]} ${height * topRight[1]}
                        ${width * bottomRight[0]} ${height * bottomRight[1]}
                        ${width * bottomLeft[0]} ${height * bottomLeft[1]}
                        z`;
    }

    /**
     * Create a string representing SVG commands to create the region of interest's border (i.e, the cutout in the center).
     * @param roiData The normalized coordinates (in percentiles) of the ROI
     * @param width The width of the current image asset
     * @param height The height of the current image asset
     */
    public buildRoiBorder(roiData: number[][], width: number, height: number): string {
        if (!roiData) {
            return '';
        }

        const [topLeft, topRight, bottomLeft, bottomRight] = roiData.sort((pointL: number[], pointR: number[]) => {
            const [xL, yL] = pointL;
            const [xR, yR] = pointR;

            return xL - xR || yL - yR;
        });

        return `M ${width * topLeft[0]} ${height * topLeft[1]}
                L ${width * topRight[0]} ${height * topRight[1]}
                ${width * bottomRight[0]} ${height * bottomRight[1]}
                ${width * bottomLeft[0]} ${height * bottomLeft[1]}
                z`;
    }
}



// WEBPACK FOOTER //
// ./src/providers/workspace/region-of-interest.ts